// Cross-fade content swap for key-driven re-renders. When the `swapKey`
// prop changes, the wrapper briefly drops opacity to 0, swaps the
// children (now keyed to the new value), and fades back up. Net effect:
// a 150 ms cross-fade between two distinct content states rather than
// a jarring hard cut.
//
// MOBILE-REDESIGN.md §11 #7: "Cross-fade content swaps. Switching
// characters in DM view: 150ms opacity cross-fade, not hard cut.
// Anchors the eye." The DM rapidly clicks between party member cards
// during a session; hard cuts make the screen feel like a slideshow,
// where cross-fades preserve continuity.
//
// Usage:
//   <CrossFade swapKey={selectedDmCharId}>
//     {selectedDmCharId && <ExpandedCharacterPanel ... />}
//   </CrossFade>
//
// Reduced motion: instant swap. The accessibility cost of forcing a
// transition on a content swap outweighs the immersion benefit.

import React, { useEffect, useRef, useState } from 'react';
import useReducedMotion from './useReducedMotion';
import { DUR_SUBTLE, EASE_STANDARD, t } from './transitions';

export default function CrossFade({ swapKey, children, durationMs = 150 }) {
  const reduceMotion = useReducedMotion();
  const [renderedChildren, setRenderedChildren] = useState(children);
  const [visible, setVisible] = useState(true);
  const lastKeyRef = useRef(swapKey);
  const swapTimerRef = useRef(null);

  useEffect(() => {
    if (lastKeyRef.current === swapKey) {
      // Same key — keep updating children inline so React reconciles
      // prop changes inside the same identity without flicker.
      setRenderedChildren(children);
      return undefined;
    }
    lastKeyRef.current = swapKey;
    if (reduceMotion) {
      setRenderedChildren(children);
      setVisible(true);
      return undefined;
    }
    // Phase 1: fade out the current content.
    setVisible(false);
    if (swapTimerRef.current) clearTimeout(swapTimerRef.current);
    swapTimerRef.current = setTimeout(() => {
      // Phase 2: swap the children (now scoped to the new key) and
      // fade them back up.
      setRenderedChildren(children);
      setVisible(true);
    }, durationMs);
    return () => { if (swapTimerRef.current) clearTimeout(swapTimerRef.current); };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [swapKey, reduceMotion, durationMs]);

  useEffect(() => () => {
    if (swapTimerRef.current) clearTimeout(swapTimerRef.current);
  }, []);

  return (
    <div
      style={{
        opacity: visible || reduceMotion ? 1 : 0,
        transition: reduceMotion ? 'none' : t(['opacity'], durationMs, EASE_STANDARD),
        // pointer-events disabled during the fade so a stray tap on
        // the outgoing content doesn't trigger an action that belongs
        // to the incoming content.
        pointerEvents: visible ? 'auto' : 'none',
      }}
    >
      {renderedChildren}
    </div>
  );
}
